import React, { memo, useState, useCallback, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { createPortal } from 'react-dom'
import { TeamRole } from 'src/interfaces/team'
import { CircularProgress, InputAdornment, TextField, Typography } from '@mui/material'
import { ChevronRightMinor, SelectMinor } from '@shopify/polaris-icons'
import { colors } from 'src/theme'
import { ModalHeader } from 'src/components/shared/ModalHeader'
import { CreateTeamRoleButton } from '../create-team-role/CreateTeamRoleButton'
import { TeamRoleItem } from './TeamRoleItem'
import { Button } from 'src/components/shared/Button'
import { useDispatch, useSelector } from 'react-redux'
import { FetchTeamPermissions, FetchTeamRoles } from 'src/constants/actionTypes'
import { removeUnderscoresAndCapitalize } from 'src/utils/baseUtils'
import { getTeamRoles } from 'src/selectors/team'
import { UserPermission } from 'src/constants/user'
import useCheckPermissions from 'src/hooks/useCheckPermissions'

interface Props {
  selectedTeamRole: TeamRole | null
  handleTeamRoleSelect(role: TeamRole): void
  modalId: string
  openModalId: string | null
  setOpenModalId: React.Dispatch<React.SetStateAction<string | null>>
  isLoading?: boolean
  disabled?: boolean
}

const SelectTeamRoleComponent = ({
  selectedTeamRole,
  handleTeamRoleSelect,
  modalId,
  openModalId,
  setOpenModalId,
  isLoading,
  disabled,
}: Props) => {
  const dispatch = useDispatch()

  const isSelectTeamRoleModalOpen = openModalId === modalId
  const portalRoot = document.getElementById('team-details')

  const teamRoles = useSelector(getTeamRoles)

  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null)

  const { hasPermission } = useCheckPermissions()

  const [isLoadingRoles, setIsLoadingRoles] = useState(true)

  const isShowLoading = isLoadingRoles || !teamRoles || isLoading

  const toggleModal = useCallback(() => {
    setOpenModalId((prev) => (prev === modalId ? null : modalId))
    setExpandedRoleId(null)
  }, [modalId, setOpenModalId])

  const handleAccordionChange = useCallback((roleId: string) => {
    setExpandedRoleId((prev) => (prev === roleId ? null : roleId))
  }, [])

  const fetchTeamRoles = useCallback(() => {
    setIsLoadingRoles(true)

    try {
      dispatch(FetchTeamRoles.request())
      dispatch(FetchTeamPermissions.request())
    } finally {
      setIsLoadingRoles(false)
    }
  }, [])

  useEffect(() => {
    fetchTeamRoles()
  }, [])

  const refreshTeamRoles = useCallback(async (): Promise<void> => {
    await fetchTeamRoles()
  }, [])

  const endAdornment = useMemo(() => {
    if (disabled) {
      return null
    }

    if (isSelectTeamRoleModalOpen) {
      return (
        <InputAdornment position="end">
          <ChevronRightMinor width={20} />
        </InputAdornment>
      )
    }

    return (
      <InputAdornment position="end">
        <SelectMinor width={20} />
      </InputAdornment>
    )
  }, [disabled])

  return (
    <>
      <div>
        <Typography color={colors.SHADES_GREY_400} variant="h6" css={tw`mb-1`}>
          Role
        </Typography>
        <TextField
          placeholder="Select role"
          value={selectedTeamRole ? removeUnderscoresAndCapitalize(selectedTeamRole.name) : ''}
          onClick={disabled ? undefined : toggleModal}
          InputProps={{
            readOnly: true,
            disabled,
            endAdornment,
          }}
          css={tw`mr-2`}
          inputProps={{ style: { cursor: disabled ? '' : 'pointer' } }}
          fullWidth
        />
      </div>
      {portalRoot &&
        isSelectTeamRoleModalOpen &&
        createPortal(
          <div css={tw`w-[440px] bg-white rounded-lg shadow-lg flex flex-col pointer-events-auto`}>
            <ModalHeader className={tw`h-11`} title="Select role" showCloseButton={false} />
            <div css={tw`flex-1 overflow-auto`}>
              {isShowLoading ? (
                <div css={tw`flex items-center justify-center h-full`}>
                  <CircularProgress />
                </div>
              ) : (
                teamRoles.map((role) => (
                  <TeamRoleItem
                    key={role.id}
                    teamRole={role}
                    selected={selectedTeamRole?.id === role.id}
                    expanded={expandedRoleId === role.id}
                    onRoleClick={handleTeamRoleSelect}
                    onAccordionChange={handleAccordionChange}
                  />
                ))
              )}
            </div>
            <div
              css={tw`p-4 gap-2 border-0 border-t border-solid border-[#EDEDED] flex justify-end flex-col`}
            >
              {hasPermission(UserPermission.RolesEdit) && (
                <CreateTeamRoleButton refreshTeamRoles={refreshTeamRoles} />
              )}
              <Button color="white" size="small" css={tw`ml-0`} onClick={toggleModal}>
                Close
              </Button>
            </div>
          </div>,
          portalRoot,
        )}
    </>
  )
}

export const SelectTeamRole = memo(SelectTeamRoleComponent)
